import React from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";

const PricingTier = ({
  name,
  price,
  description,
  features,
  highlighted = false,
  buttonText = "Get Started",
  buttonLink = "/demo",
  perMonth,
  popularLabel,
}: {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  buttonText?: string;
  buttonLink?: string;
  perMonth: string;
  popularLabel?: string;
}) => {
  return (
    <div
      className={`p-4 sm:p-6 md:p-8 rounded-xl border ${
        highlighted
          ? "border-brand-300 bg-card shadow-lg"
          : "border-border bg-card"
      }`}
    >
      {highlighted && popularLabel && (
        <div className="px-3 py-1 text-xs sm:text-sm text-brand-700 dark:text-brand-300 bg-brand-100 dark:bg-brand-900/30 rounded-full inline-block mb-3 md:mb-4">
          {popularLabel}
        </div>
      )}
      <h3 className="text-lg sm:text-xl font-bold mb-2 text-foreground">{name}</h3>
      <div className="mb-3 md:mb-4">
        <span className="text-2xl sm:text-3xl font-bold text-foreground">{price}</span>
        {price !== "Custom" && <span className="text-sm sm:text-base text-muted-foreground">{perMonth}</span>}
      </div>
      <p className="text-sm sm:text-base text-muted-foreground mb-4 md:mb-6">{description}</p>
      <ul className="space-y-2 sm:space-y-3 mb-6 md:mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check
              size={16}
              className={`mr-2 mt-0.5 flex-shrink-0 ${
                highlighted ? "text-brand-500" : "text-foreground"
              }`}
            />
            <span className="text-sm sm:text-base text-foreground">{feature}</span>
          </li>
        ))}
      </ul>
      <Link to={buttonLink}>
        <Button
          className={`w-full ${
            highlighted
              ? "gradient-bg hover:bg-brand-700"
              : "bg-muted hover:bg-muted/80 text-foreground"
          }`}
        >
          {buttonText}
        </Button>
      </Link>
    </div>
  );
};

const Pricing = () => {
  const { t } = useTranslation();
  
  return (
    <section className="section-padding">
      <div className="container mx-auto px-4 max-w-full">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4 text-foreground">
            {t("pricing.title")}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground px-2">
            {t("pricing.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 w-full">
          <PricingTier
            name={t("pricing.plans.basic.name")}
            price={t("pricing.plans.basic.price")}
            description={t("pricing.plans.basic.description")}
            features={[
              t("pricing.plans.basic.features.0"),
              t("pricing.plans.basic.features.1"),
              t("pricing.plans.basic.features.2"),
              t("pricing.plans.basic.features.3"),
              t("pricing.plans.basic.features.4"),
              t("pricing.plans.basic.features.5"),
            ]}
            buttonText={t("pricing.getStarted")}
            perMonth={t("pricing.perMonth")}
          />

          <PricingTier
            name={t("pricing.plans.pro.name")}
            price={t("pricing.plans.pro.price")}
            description={t("pricing.plans.pro.description")}
            features={[
              t("pricing.plans.pro.features.0"),
              t("pricing.plans.pro.features.1"),
              t("pricing.plans.pro.features.2"),
              t("pricing.plans.pro.features.3"),
              t("pricing.plans.pro.features.4"),
              t("pricing.plans.pro.features.5"),
              t("pricing.plans.pro.features.6"),
            ]}
            highlighted={true}
            buttonText={t("pricing.getStarted")}
            perMonth={t("pricing.perMonth")}
            popularLabel={t("pricing.plans.pro.popular")}
          />

          <PricingTier
            name={t("pricing.plans.enterprise.name")}
            price={t("pricing.plans.enterprise.price")}
            description={t("pricing.plans.enterprise.description")}
            features={[
              t("pricing.plans.enterprise.features.0"),
              t("pricing.plans.enterprise.features.1"),
              t("pricing.plans.enterprise.features.2"),
              t("pricing.plans.enterprise.features.3"),
              t("pricing.plans.enterprise.features.4"),
              t("pricing.plans.enterprise.features.5"),
              t("pricing.plans.enterprise.features.6"),
            ]}
            buttonText={t("pricing.contactSales")}
            buttonLink="/contact"
            perMonth={t("pricing.perMonth")}
          />
        </div>

        <div className="mt-10 md:mt-16 p-4 sm:p-6 md:p-8 bg-muted rounded-xl text-center max-w-3xl mx-auto">
          <h3 className="text-lg sm:text-xl font-semibold mb-2 md:mb-3 text-foreground">
            {t("pricing.callUs")}
          </h3>
          <p className="text-sm sm:text-base text-foreground/70 mb-4 md:mb-6">
            {t("pricing.callUsDesc")}
          </p>
          <Link to="/contact">
            <Button variant="outline" size="lg" className="w-full sm:w-auto">
              {t("pricing.consultation")}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
